import { useEffect } from 'react'
import { RotateCcw, Trash2, X, XCircle } from 'lucide-react'
import type { EventStatus } from '../types'

type Props = {
  status: EventStatus
  title: string
  // Edits in the form not saved yet, lost on leaving.
  unsaved: boolean
  busy: boolean
  onConfirm: () => void
  onClose: () => void
}

// What changing the whole event does, said before it happens (PRD.md, item
// 36): canceling keeps it in the app as Cancelado and tells who saved it;
// deleting takes it out and tells no one (items 33 and 35).
const ACTIONS: Record<EventStatus, { heading: string; text: string; hint?: string; confirm: string; danger: boolean }> = {
  canceled: {
    heading: 'Cancelar evento?',
    text: 'O evento continua no app, como Cancelado, em todos os locais e horários. Quem favoritou ou adicionou à agenda é avisado.',
    hint: 'Para cancelar só um local, use Cancelar neste local, no card dele.',
    confirm: 'Cancelar evento',
    danger: true,
  },
  deleted: {
    heading: 'Excluir evento?',
    text: 'O evento sai do app: do mapa, da busca e dos Favoritos, e o link abre “Evento não disponível”. Ninguém é avisado.',
    hint: 'Use para um evento que não deveria estar no Guve, como um erro ou um duplicado. Republicar traz de volta.',
    confirm: 'Excluir evento',
    danger: true,
  },
  published: {
    heading: 'Republicar evento?',
    text: 'O evento volta ao app, com os locais e horários que tem.',
    confirm: 'Republicar',
    danger: false,
  },
}

export function EventStatusDialog({ status, title, unsaved, busy, onConfirm, onClose }: Props) {
  const action = ACTIONS[status]

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [busy, onClose])

  return (
    <div className="dialog-backdrop" role="presentation" onClick={() => !busy && onClose()}>
      <section className="dialog dialog--compact" role="dialog" aria-modal="true" aria-labelledby="status-title" onClick={(event) => event.stopPropagation()}>
        <div className="dialog__heading">
          <div>
            <h2 id="status-title">{action.heading}</h2>
            <p><strong>{title}</strong></p>
          </div>
          <button type="button" className="icon-button" aria-label="Fechar" disabled={busy} onClick={onClose}><X size={18} /></button>
        </div>
        <div className="dialog__body">
          <p>{action.text}</p>
          {action.hint && <p>{action.hint}</p>}
          {unsaved && <p>As alterações não salvas neste formulário são descartadas.</p>}
        </div>
        <div className="dialog__actions">
          <button type="button" className="button button--secondary" disabled={busy} onClick={onClose}>Voltar</button>
          <button type="button" className={`button ${action.danger ? 'button--destructive' : 'button--primary'}`} disabled={busy} onClick={onConfirm}>
            {status === 'deleted' ? <Trash2 size={17} /> : status === 'canceled' ? <XCircle size={17} /> : <RotateCcw size={17} />}
            {busy ? 'Salvando…' : action.confirm}
          </button>
        </div>
      </section>
    </div>
  )
}
